import type { AuditorRecord } from "../api/client";
import { Callout } from "../components/Callout";

/**
 * Screen 18 — the competence record, submitted.
 *
 * "What you asserted" is the point of this screen. The auditor has just made a set of
 * claims about their own competence and signed them; showing those claims back is what
 * makes the certification body's later verification meaningful — it is the same record,
 * read by the person who made it.
 *
 * The status shown is `awaiting_verification` and never `active`. Self-assertion does not
 * make an auditor allocatable; Cl. 7 makes that the certification body's determination.
 */
export function RecordSubmitted({ record }: { record: AuditorRecord }) {
  const competence = record.competence ?? [];
  const exclusions = record.exclusions ?? [];

  return (
    <>
      <div className="eyebrow">Auditor onboarding</div>
      <h1>Competence record submitted</h1>

      <Callout
        tone="info"
        title={`Sent to ${record.tenant_name ?? "your certification body"} for verification.`}
      >
        You will not appear as an allocatable lead auditor until the technical manager
        verifies this record. That verification, not your own assertion, is what ISO/IEC
        17021-1 Cl.&nbsp;7 requires.
      </Callout>

      <div className="card">
        <h2>What you asserted</h2>
        <dl className="kv">
          <dt>Name</dt>
          <dd>
            {record.name}
            {record.position ? ` — ${record.position}` : ""}
          </dd>

          <dt>Engagement</dt>
          <dd>{record.engagement ?? "—"}</dd>

          <dt>Competence claimed</dt>
          <dd>
            {competence.length === 0
              ? "—"
              : competence.map((claim) => (
                  <div key={claim.standard}>
                    {claim.standard}{" "}
                    <span className="sub">
                      as {claim.level.toLowerCase()}, {(claim.sectors ?? []).length}{" "}
                      {(claim.sectors ?? []).length === 1 ? "sector" : "sectors"}
                    </span>
                  </div>
                ))}
          </dd>

          <dt>Audit experience</dt>
          <dd>
            {record.audits_led ?? 0} audits led · {record.audit_days ?? 0} audit days ·{" "}
            {record.ms_years ?? 0} years in management systems
          </dd>

          <dt>Excluded clients</dt>
          <dd>
            {exclusions.length === 0
              ? "None declared"
              : exclusions.map((entry) => (
                  <div key={entry.organisation_name}>{entry.organisation_name}</div>
                ))}
          </dd>

          <dt>Signed</dt>
          <dd>
            {record.signature} · {formatMoment(record.signed_at)}
          </dd>
        </dl>
      </div>

      {/* Recorded, not refused — and said here because the auditor should learn the
          consequence from their own record rather than from a blocked allocation later. */}
      {record.ai_accountability === false ? (
        <Callout tone="warn" title="AI accountability is not accepted.">
          You cannot be allocated to an audit until it is. Ask your certification body to
          return the record if you want to change that answer.
        </Callout>
      ) : null}
    </>
  );
}

function formatMoment(value: string | null | undefined): string {
  if (!value) return "—";
  const when = new Date(value);
  return Number.isNaN(when.getTime())
    ? "—"
    : when.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}
